import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API_BASE = `${BACKEND_URL}/api`;

const api = axios.create({
  baseURL: API_BASE,
});

// Add auth token to requests
api.interceptors.request.use((config) => {
  const token = localStorage.getItem('token');
  if (token) {
    config.headers.Authorization = `Bearer ${token}`;
  }
  return config;
});

// Handle auth errors
//
// Endpoints in ``SKIP_401_REDIRECT`` intentionally render their OWN 401
// UX (login form errors, "welcome link expired" card, etc.) and must NOT
// trigger the global "clear token + redirect to /" behaviour — otherwise
// the caller's error state would be nuked before the user sees it.
//
// Add a new endpoint here rather than chaining another ``.includes()``
// onto the interceptor.
const SKIP_401_REDIRECT = [
  '/auth/login',
  '/auth/register',
  '/auth/welcome-exchange',
  '/auth/welcome-login',
];

api.interceptors.response.use(
  (response) => response,
  (error) => {
    const url = error.config?.url || '';
    const isAuthEndpoint = SKIP_401_REDIRECT.some((path) => url.includes(path));

    if (error.response?.status === 401 && !isAuthEndpoint) {
      localStorage.removeItem('token');
      localStorage.removeItem('user');
      window.location.href = '/';
    }
    return Promise.reject(error);
  }
);

/**
 * Extract a human-readable error message from API error responses
 * Handles both string and Pydantic validation error array formats
 */
export const getErrorMessage = (error, fallback = 'An error occurred') => {
  const detail = error.response?.data?.detail;
  
  if (!detail) return fallback;
  
  // If detail is a string, return it directly
  if (typeof detail === 'string') return detail;
  
  // If detail is an array (Pydantic validation errors), extract messages
  if (Array.isArray(detail)) {
    const messages = (detail || []).map(err => {
      if (typeof err === 'string') return err;
      if (err.msg) return err.msg;
      return JSON.stringify(err);
    });
    return messages.join(', ') || fallback;
  }
  
  // If detail is an object with a message property
  if (typeof detail === 'object' && detail.msg) {
    return detail.msg;
  }
  
  return fallback;
};

export default api;

export const auth = {
  register: (data) => api.post('/auth/register', data),
  login: (data) => api.post('/auth/login', data),
  // welcomeExchange uses a one-shot UUID ({wt}) — no email/password in the
  // URL or body, so Chrome's password manager has nothing to detect or save.
  welcomeExchange: (data) => api.post('/auth/welcome-exchange', data),
  // welcomeLogin (legacy {email, token}) — kept for older email links already in inboxes.
  welcomeLogin: (data) => api.post('/auth/welcome-login', data),
  me: () => api.get('/auth/me'),
  requestPasswordReset: (data) => api.post('/auth/password-reset/request', data),
  confirmPasswordReset: (data) => api.post('/auth/password-reset/confirm', data),
  changePassword: (data) => api.put('/auth/password/change', data),
};

export const charities = {
  list: () => api.get('/charities'),
  listPublic: () => api.get('/charities/public'),
  byReferral: (code) => api.get(`/charities/public/referral/${encodeURIComponent(code)}`),
  create: (data) => api.post('/charities', data),
};

export const rshd = {
  list: (category) => api.get('/rshd/items', { params: { category } }),
  myItems: () => api.get('/rshd/my-items'),
  create: (data) => api.post('/rshd/items', data),
  createBatch: (items) => api.post('/rshd/items/batch', { items }),
  update: (id, data) => api.put(`/rshd/items/${id}`, data),
  delete: (id) => api.delete(`/rshd/items/${id}`),
  // Draft management (Phase 1)
  drafts: (params) => api.get('/rshd/drafts', { params }),
  getDraft: (id) => api.get(`/rshd/drafts/${id}`),
  createDraft: (data) => api.post('/rshd/drafts', data),
  updateDraft: (id, data) => api.put(`/rshd/drafts/${id}`, data),
  deleteDraft: (id) => api.delete(`/rshd/drafts/${id}`),
  validateDrafts: (draftIds) => api.post('/rshd/drafts/validate', draftIds),
  confirmDrafts: (data) => api.post('/rshd/drafts/confirm', data),
  rejectDrafts: (data) => api.post('/rshd/drafts/reject', data),
};

export const favorites = {
  list: () => api.get('/favorites'),
  create: (data) => api.post('/favorites', data),
  delete: (id) => api.delete(`/favorites/${id}`),
};

// Item-level favorites (Enhanced DACFI-List)
export const favoriteItems = {
  list: () => api.get('/favorites/items'),
  create: (data) => api.post('/favorites/items', data),
  delete: (itemName) => api.post('/favorites/items/delete', { item_name: itemName }),
};

// User settings
export const userSettings = {
  updateAutoThreshold: (data) => api.put('/users/settings/auto-threshold', data),
  getCharityOfChoice: () => api.get('/dac/charity-of-choice'),
  updateCharityOfChoice: (data) => api.put('/dac/charity-of-choice', data),
};

// Retailer Settings Hub (V1.1)
export const retailerSettings = {
  getStoreInfo: () => api.get('/retailer/settings/store-info'),
  updateStoreInfo: (data) => api.put('/retailer/settings/store-info', data),
  uploadStoreLogo: (file) => {
    const fd = new FormData(); fd.append('file', file);
    return api.post('/retailer/settings/store-logo', fd, { headers: { 'Content-Type': 'multipart/form-data' } });
  },
  uploadStoreImage: (file) => {
    const fd = new FormData(); fd.append('file', file);
    return api.post('/retailer/settings/store-image', fd, { headers: { 'Content-Type': 'multipart/form-data' } });
  },
  getPickupDelivery: () => api.get('/retailer/settings/pickup-delivery'),
  updatePickupDelivery: (data) => api.put('/retailer/settings/pickup-delivery', data),
  getNotifications: () => api.get('/retailer/settings/notifications'),
  updateNotifications: (notification_prefs) => api.put('/retailer/settings/notifications', { notification_prefs }),
  changePassword: (current_password, new_password) => api.post('/retailer/settings/security/change-password', { current_password, new_password }),
  listTeam: () => api.get('/retailer/settings/team'),
  inviteTeamMember: (data) => api.post('/retailer/settings/team/invite', data),
  removeTeamMember: (memberId) => api.delete(`/retailer/settings/team/${memberId}`),
};

// Corporate / Multi-Location (V1.1 Hybrid Option A)
export const corporate = {
  getProfile: () => api.get('/corporate/profile'),
  updateProfile: (data) => api.put('/corporate/profile', data),
  listLocations: () => api.get('/corporate/locations'),
  createLocation: (data) => api.post('/corporate/locations', data),
  getLocation: (id) => api.get(`/corporate/locations/${id}`),
  updateLocation: (id, data) => api.put(`/corporate/locations/${id}`, data),
  archiveLocation: (id) => api.delete(`/corporate/locations/${id}`),
  togglePayoutOverride: (id, payout_inherits_from_corporate) =>
    api.post(`/corporate/locations/${id}/payout-override`, { payout_inherits_from_corporate }),
};

// DAC Retailers (DACDRLP-List management)
export const dacRetailers = {
  list: () => api.get('/dac/retailers'),
  add: (drlpId) => api.post(`/dac/retailers/add?drlp_id=${drlpId}`),
  remove: (drlpId) => api.delete(`/dac/retailers/${drlpId}`),
  updateDacsai: (radius, deliveryLocation = null) => {
    let url = `/dac/dacsai?dacsai_rad=${radius}`;
    if (deliveryLocation) {
      return api.put(url, deliveryLocation);
    }
    return api.put(url);
  },
  updateLocation: (deliveryLocation) => api.put('/dac/location', deliveryLocation),
};

export const notifications = {
  list: () => api.get('/notifications'),
  markRead: (id) => api.put(`/notifications/${id}/read`),
  // Push notification subscription
  subscribePush: (subscription) => api.post('/notifications/subscribe', subscription),
  unsubscribePush: (endpoint) => api.delete('/notifications/subscribe', { params: { endpoint } }),
  getSubscriptionStatus: () => api.get('/notifications/subscription-status'),
  getPreferences: () => api.get('/notifications/preferences'),
  updatePreferences: (prefs) => api.put('/notifications/preferences', prefs),
};

// Email Digest & Notification Preferences
export const emailDigest = {
  preview: () => api.get('/email/digest/preview'),
  send: () => api.post('/email/digest/send'),
  subscribe: () => api.post('/email/digest/subscribe'),
  unsubscribe: () => api.post('/email/digest/unsubscribe'),
  history: (limit = 10) => api.get('/email/digest/history', { params: { limit } }),
  getPreferences: () => api.get('/email/notifications/preferences'),
  updatePreferences: (prefs) => api.put('/email/notifications/preferences', prefs),
  // Weekly Green Digest (food-diversion sustainability recap)
  greenPreview: () => api.get('/email/green-digest/preview'),
  greenSend: () => api.post('/email/green-digest/send'),
  greenSendAll: () => api.post('/email/green-digest/send-all'),
};

export const orders = {
  list: () => api.get('/orders'),
  create: (data) => api.post('/orders', data),
  taxQuote: (data) => api.post('/orders/tax-quote', data),
  updateStatus: (orderId, status) => api.put(`/orders/${orderId}/status`, { status }),
  getDeliveryWindow: (drlpId) => api.get(`/orders/delivery-window/${drlpId}`),
  getRetailerFulfillment: (drlpId) => api.get(`/orders/retailer/${drlpId}/fulfillment`),
  // Fetch a retailer's public profile (store hours, logo, etc.) — used by
  // ConsumerCheckout's StoreHoursBanner. The directory router has a
  // '/directory' prefix so this endpoint sits at /api/directory/retailers/…
  // (public-safe: no PII exposed).
  getRetailerProfile: (drlpId) => api.get(`/directory/retailers/${drlpId}/public-profile`),
  // New DRLP acknowledgment flow
  acknowledgeOrder: (orderId, confirmedReadyBy) => api.post(`/orders/${orderId}/drlp-acknowledge`, { confirmed_ready_by: confirmedReadyBy }),
  markReady: (orderId, readyNotes = '') => api.post(`/orders/${orderId}/drlp-ready`, { ready_notes: readyNotes }),
  cancelOrder: (orderId) => api.post(`/orders/${orderId}/cancel`),
  getStatusDetails: (orderId) => api.get(`/orders/${orderId}/status-details`),
};

// Admin incident reports
export const incidents = {
  list: (filters = {}) => {
    const params = new URLSearchParams();
    Object.entries(filters).forEach(([key, value]) => {
      if (value !== undefined && value !== null) params.append(key, value);
    });
    return api.get(`/admin/incidents?${params.toString()}`);
  },
  stats: (days = 7) => api.get(`/admin/incidents/stats?days=${days}`),
  get: (incidentId) => api.get(`/admin/incidents/${incidentId}`),
  update: (incidentId, data) => api.put(`/admin/incidents/${incidentId}`, data),
  dismiss: (incidentId) => api.post(`/admin/incidents/${incidentId}/dismiss`),
};

export const drlp = {
  locations: () => api.get('/drlp/locations'),
  locationsEnriched: () => api.get('/drlp/locations/enriched'),
  myLocation: () => api.get('/drlp/my-location'),
  createLocation: (data) => api.post('/drlp/locations', data),
};

export const stores = {
  all: (userLat, userLng, maxDistance = 50) => {
    const params = new URLSearchParams();
    if (userLat) params.append('user_lat', userLat);
    if (userLng) params.append('user_lng', userLng);
    if (maxDistance) params.append('max_distance', maxDistance);
    return api.get(`/stores/all?${params.toString()}`);
  },
};

export const admin = {
  stats: () => api.get('/admin/stats'),
  users: () => api.get('/admin/users'),
  items: () => api.get('/admin/items'),
  analytics: () => api.get('/admin/platform-analytics'),
  userDetails: (userId) => api.get(`/admin/users/${userId}`),
  updateUserStatus: (userId, status) => api.put(`/admin/users/${userId}/status`, { status }),
  updateItemStatus: (itemId, status) => api.put(`/admin/items/${itemId}/status`, { status }),
  alerts: () => api.get('/admin/alerts'),
  activity: () => api.get('/admin/activity'),
  charities: () => api.get('/admin/charities'),
  createAdmin: (userData) => api.post('/admin/create-admin', userData),
  // Retailer Management
  retailers: () => api.get('/admin/retailers'),
  retailerDetails: (retailerId) => api.get(`/admin/retailers/${retailerId}`),
  retailerAnalytics: () => api.get('/admin/retailers/analytics/overview'),
  updateRetailerStatus: (retailerId, status) => api.put(`/admin/retailers/${retailerId}/status`, { status }),
  // Sandbox/Onboarding Approvals
  pendingApprovals: () => api.get('/admin/retailers/pending-approvals'),
  approveRegistration: (retailerId) => api.put(`/admin/retailers/${retailerId}/approve-registration`),
  rejectRegistration: (retailerId, reason) => api.put(`/admin/retailers/${retailerId}/reject-registration`, { reason }),
  approveGoLive: (retailerId) => api.put(`/admin/retailers/${retailerId}/approve-go-live`),
  rejectGoLive: (retailerId, reason) => api.put(`/admin/retailers/${retailerId}/reject-go-live`, { reason }),
  // Charity Application Management
  charityApplications: (status) => api.get('/admin/charity-applications', { params: status ? { status } : {} }),
  charityApplicationDetails: (appId) => api.get(`/admin/charity-applications/${appId}`),
  approveCharityApplication: (appId) => api.post(`/admin/charity-applications/${appId}/approve`),
  rejectCharityApplication: (appId, reason) => api.post(`/admin/charity-applications/${appId}/reject`, { reason }),
  requestCharityInfo: (appId, message) => api.post(`/admin/charity-applications/${appId}/request-info`, { message }),
  // Charity Payout Management
  charityPayouts: () => api.get('/admin/charity-payouts'),
  charityPayoutDetails: (charityId) => api.get(`/admin/charity-payouts/${charityId}`),
  initiateCharityPayout: (charityId, amount) => api.post(`/admin/charity-payouts/${charityId}/initiate`, { amount }),
  confirmManualPayout: (payoutId) => api.post(`/admin/charity-payouts/${payoutId}/confirm-manual`),
  // Retailer Payout Management
  retailerPayouts: () => api.get('/admin/retailer-payouts'),
  retailerPayoutDetails: (retailerId) => api.get(`/admin/retailer-payouts/${retailerId}`),
  initiateRetailerPayout: (retailerId, amount) => api.post(`/admin/retailer-payouts/${retailerId}/initiate`, { amount }),
  confirmRetailerPayout: (payoutId) => api.post(`/admin/retailer-payouts/${payoutId}/confirm-manual`),
  runFridayPayoutBatch: () => api.post('/admin/retailer-payouts/run-friday-batch'),
  // Pilot Health Dashboard
  pilotHealth: () => api.get('/admin/pilot-health'),
  // Multi-Location: promote/demote a single retailer to a Corporate parent
  promoteUserToCorporate: (userId, data = {}) =>
    api.post(`/admin/users/${userId}/promote-to-corporate`, data),
  demoteUserFromCorporate: (userId) =>
    api.post(`/admin/users/${userId}/demote-from-corporate`),

  // Refund & Settlement System v1.2 — admin surfaces
  listRefunds: (params = {}) => api.get('/admin/refunds', { params }),
  createRefund: (payload) => api.post('/admin/refunds', payload),
  uploadRefundEvidence: (adjustmentId, formData) =>
    api.post(`/admin/refunds/${adjustmentId}/evidence`, formData, {
      headers: { 'Content-Type': 'multipart/form-data' },
    }),
  buildSettlement: (payload) => api.post('/admin/settlements/build', payload),
  listDisputes: (params = {}) => api.get('/admin/disputes', { params }),
  resolveDispute: (disputeId, payload) =>
    api.post(`/admin/disputes/${disputeId}/resolve`, payload),

  // Reconciliation Log System (Phase 1 · sub-phase c)
  listReconciliation: (params = {}) => api.get('/admin/reconciliation', { params }),
  getReconciliation: (id) => api.get(`/admin/reconciliation/${id}`),
  buildReconciliation: (payload) => api.post('/admin/reconciliation/build', payload),
  downloadReconciliation: (id, format = 'csv') =>
    api.get(`/admin/reconciliation/${id}/download`, { params: { format }, responseType: 'blob' }),
  emailReconciliation: (id) => api.post(`/admin/reconciliation/${id}/email`),
  pushReconciliation: (id) => api.post(`/admin/reconciliation/${id}/push`),

  // DRLP API key management (Phase 1 · sub-phase d)
  listDrlps: () => api.get('/admin/drlp/list'),
  issueDrlpKey: (drlpId, label = '') => api.post(`/admin/drlp/${drlpId}/api-key`, { label }),
  getDrlpKey: (drlpId) => api.get(`/admin/drlp/${drlpId}/api-key`),
  revokeDrlpKey: (drlpId) => api.delete(`/admin/drlp/${drlpId}/api-key`),

  // ST-1 filing automation (Phase 1 · sub-phase e)
  st1Preview: (year, month) => api.get('/admin/st1/preview', { params: { year, month } }),
  st1Generate: (payload) => api.post('/admin/st1/generate', payload),
  st1Filings: () => api.get('/admin/st1/filings'),
  st1Download: (period, mode) =>
    api.get(`/admin/st1/filings/${period}/${mode}/download`, { responseType: 'blob' }),
  st1RecordConfirmation: (period, mode, payload) =>
    api.post(`/admin/st1/filings/${period}/${mode}/confirmation`, payload),
  st1MarkIFileUploaded: (period, mode, uploaded) =>
    api.post(`/admin/st1/filings/${period}/${mode}/ifile-uploaded`, { uploaded }),

  // Seed Cleanup — safe soft-archive of dummy/seed retailers
  seedCleanupCandidates: () => api.get('/admin/seed-cleanup/candidates'),
  seedCleanupArchive: (retailer_ids) => api.post('/admin/seed-cleanup/archive', { retailer_ids }),
  seedCleanupRestore: (retailer_ids) => api.post('/admin/seed-cleanup/restore', { retailer_ids }),
  // Reconciliation Exception Report
  taxExceptions: (period, checkStripe = false) =>
    api.get('/admin/tax/exceptions', { params: { period_yyyymm: period, check_stripe: checkStripe } }),
  // Onboarding drill-down reminder
  sendOnboardingReminder: (retailerId) =>
    api.post(`/admin/retailers/${retailerId}/send-onboarding-reminder`),
};

// Retailer (DRLP) APIs
export const retailer = {
  launchReadiness: () => api.get('/retailers/launch-readiness'),
  requestGoLive: () => api.put('/retailers/request-go-live'),
  updateProfile: (profileData) => api.put('/retailers/profile', profileData),
  getProfile: () => api.get('/retailers/profile'),
  // Payout APIs
  getPayoutStatus: () => api.get('/retailer/payouts/status'),
  createStripeConnectLink: () => api.post('/retailer/payouts/stripe/connect'),
  getStripeStatus: () => api.get('/retailer/payouts/stripe/status'),
  submitBankDetails: (details) => api.post('/retailer/payouts/manual-bank', details),

  // Refund & Settlement System v1.2 — retailer surfaces
  listRefunds: () => api.get('/retailer/refunds'),
  listSettlements: () => api.get('/retailer/settlements'),
  listDisputes: () => api.get('/retailer/disputes'),
  fileDispute: (payload) => api.post('/retailer/disputes', payload),
  uploadDisputeEvidence: (disputeId, formData) =>
    api.post(`/retailer/disputes/${disputeId}/evidence`, formData, {
      headers: { 'Content-Type': 'multipart/form-data' },
    }),
  getWalletMtd: () => api.get('/retailer/wallet/mtd'),

  // Reconciliation Log System (Phase 1 · sub-phase c)
  listReconciliation: (params = {}) => api.get('/retailer/reconciliation', { params }),
  getReconciliation: (id) => api.get(`/retailer/reconciliation/${id}`),
  downloadReconciliation: (id, format = 'csv') =>
    api.get(`/retailer/reconciliation/${id}/download`, { params: { format }, responseType: 'blob' }),

  // DRLP webhook config (Phase 1 · sub-phase d)
  getWebhookConfig: () => api.get('/retailer/webhook-config'),
  setWebhookConfig: (payload) => api.put('/retailer/webhook-config', payload),
  testWebhook: () => api.post('/retailer/webhook-config/test'),
  getWebhookDeliveries: (limit = 50) => api.get('/retailer/webhook-deliveries', { params: { limit } }),
};

// DRLP Notifications
export const drlpNotifications = {
  list: (unreadOnly = false, limit = 50) => api.get(`/notifications/drlp?unread_only=${unreadOnly}&limit=${limit}`),
  unreadCount: () => api.get('/notifications/drlp/unread-count'),
  markRead: (notificationId) => api.post(`/notifications/drlp/${notificationId}/read`),
  markAllRead: () => api.post('/notifications/drlp/mark-all-read'),
};

// DAC (Consumer) Order Notifications
export const dacNotifications = {
  list: (unreadOnly = false, limit = 50) => api.get(`/notifications/dac?unread_only=${unreadOnly}&limit=${limit}`),
  unreadCount: () => api.get('/notifications/dac/unread-count'),
  markRead: (notificationId) => api.post(`/notifications/dac/${notificationId}/read`),
  markAllRead: () => api.post('/notifications/dac/mark-all-read'),
};

export const categories = {
  list: () => api.get('/categories'),
};

// Barcode and OCR services
export const barcode = {
  lookup: (barcodeNumber) => api.post('/barcode/lookup', { barcode: barcodeNumber }),
  scanImage: (imageBase64) => api.post('/barcode/scan-image', { image_base64: imageBase64 }),
};

export const ocr = {
  extractPrice: (imageBase64, prompt = null) => api.post('/ocr/extract-price', { 
    image_base64: imageBase64,
    prompt: prompt 
  }),
  analyzeProduct: (imageBase64) => api.post('/ocr/analyze-product', { 
    image_base64: imageBase64 
  }),
};

// Push Notifications (FCM)
export const push = {
  register: (token) => api.post('/push/register', { token }),
  unregister: (token) => api.delete('/push/unregister', { data: { token } }),
  status: () => api.get('/push/status'),
  test: () => api.post('/push/test'),
};

// ============================================================
// UVI-RSHD Vision Intelligence APIs (Phase 1.5)
// ============================================================
export const vision = {
  // Process image and generate drafts
  process: (file, options = {}, signal = null) => {
    const formData = new FormData();
    formData.append('file', file);
    if (options.runOcr !== undefined) formData.append('run_ocr', options.runOcr);
    if (options.runFreshness !== undefined) formData.append('run_freshness', options.runFreshness);
    if (options.saveCrops !== undefined) formData.append('save_crops', options.saveCrops);
    return api.post('/vision/process', formData, {
      headers: { 'Content-Type': 'multipart/form-data' },
      signal: signal,
    });
  },

  // Process base64 image (for camera capture)
  processBase64: (imageBase64, options = {}) => {
    const formData = new FormData();
    formData.append('image_base64', imageBase64);
    if (options.runOcr !== undefined) formData.append('run_ocr', options.runOcr);
    if (options.runFreshness !== undefined) formData.append('run_freshness', options.runFreshness);
    if (options.saveCrops !== undefined) formData.append('save_crops', options.saveCrops);
    return api.post('/vision/process-base64', formData);
  },

  // ==========================================================================
  // PHASE 1.75 VIDEO PROCESSING
  // ==========================================================================

  // Process video and generate drafts (Phase 1.75)
  processVideo: (file, options = {}, signal = null) => {
    const formData = new FormData();
    formData.append('file', file);
    // Extraction FPS (1-10, default 4)
    if (options.extractionFps !== undefined) {
      formData.append('extraction_fps', options.extractionFps);
    }
    if (options.runOcr !== undefined) formData.append('run_ocr', options.runOcr);
    if (options.runFreshness !== undefined) formData.append('run_freshness', options.runFreshness);
    if (options.saveCrops !== undefined) formData.append('save_crops', options.saveCrops);
    return api.post('/video/ingest', formData, {
      headers: { 'Content-Type': 'multipart/form-data' },
      // Video processing can take 10-20 minutes for longer videos (15 minutes timeout)
      timeout: 900000,
      signal: signal,
    });
  },

  // Get video processing status
  videoStatus: (videoId) => api.get(`/video/status/${videoId}`),

  // Get video processing history
  videoHistory: (limit = 20) => api.get('/video/history', { params: { limit } }),

  // Detection preview only (no draft generation)
  detectOnly: (file) => {
    const formData = new FormData();
    formData.append('file', file);
    return api.post('/vision/detect-only', formData, {
      headers: { 'Content-Type': 'multipart/form-data' },
    });
  },

  // Process single item
  singleItem: (file, productHint = null) => {
    const formData = new FormData();
    formData.append('file', file);
    if (productHint) formData.append('product_hint', productHint);
    return api.post('/vision/single-item', formData, {
      headers: { 'Content-Type': 'multipart/form-data' },
    });
  },

  // Get pending drafts (mobile-optimized)
  pendingDrafts: (params = {}) => api.get('/vision/drafts/pending', { params }),

  // Quick edit a draft field
  quickEdit: (draftId, updates) => api.put(`/vision/drafts/${draftId}/quick-edit`, updates),

  // Bulk confirm drafts
  bulkConfirm: (data) => api.post('/vision/drafts/bulk-confirm', data),

  // Get batch history
  batches: (limit = 20) => api.get('/vision/batches', { params: { limit } }),

  // Generate drafts from previous detection
  generateDrafts: (imageId, imageBase64, detectedItems) => {
    const formData = new FormData();
    formData.append('image_id', imageId);
    formData.append('image_base64', imageBase64);
    formData.append('detected_items', JSON.stringify(detectedItems));
    return api.post('/vision/generate-drafts', formData);
  },
};

// Badges API
export const badges = {
  // Get badge definitions
  definitions: () => api.get('/badges/definitions'),
  
  // Get current user's badges
  myBadges: () => api.get('/badges/my-badges'),
  
  // Get another user's public badges
  userBadges: (userId) => api.get(`/badges/user/${userId}`),
  
  // Check and award new badges
  checkAndAward: () => api.post('/badges/check-and-award'),
  
  // Get leaderboard
  leaderboard: (limit = 10) => api.get('/badges/leaderboard', { params: { limit } }),
};

// Impact KPIs API (Savings & GHG Aversion)
export const impact = {
  // Consumer endpoints
  consumerSummary: () => api.get('/impact/consumer/summary'),
  consumerMonthly: (months = 12) => api.get('/impact/consumer/monthly', { params: { months } }),
  consumerCategoryBreakdown: () => api.get('/impact/consumer/breakdown/category'),
  consumerRetailerBreakdown: () => api.get('/impact/consumer/breakdown/retailer'),
  consumerCertificate: () => api.get('/impact/certificate/consumer'),
  
  // Retailer endpoints
  retailerSummary: () => api.get('/impact/retailer/summary'),
  retailerWeekly: (weeks = 12) => api.get('/impact/retailer/weekly', { params: { weeks } }),
  retailerTransactions: (limit = 20) => api.get('/impact/retailer/transactions', { params: { limit } }),
  retailerCertificate: () => api.get('/impact/certificate/retailer'),
  monthlyReport: (month) => api.get('/impact/retailer/monthly-report', { params: month ? { month } : {}, responseType: 'blob' }),
  
  // Admin endpoints
  platformSummary: () => api.get('/impact/admin/platform-summary'),
  topRetailers: (limit = 10, sortBy = 'ghg') => api.get('/impact/admin/top-retailers', { params: { limit, sort_by: sortBy } }),
  consumerLeaderboard: (limit = 10) => api.get('/impact/admin/consumer-leaderboard', { params: { limit } }),
  
  // Calculate order impact at checkout
  calculateOrder: (items) => api.post('/impact/calculate-order', items),
};

// DoorDash Delivery API
export const delivery = {
  // Dispatch a delivery (Retailer only)
  dispatch: (data) => api.post('/delivery/dispatch', data),
  
  // Get delivery status
  getStatus: (externalDeliveryId) => api.get(`/delivery/status/${externalDeliveryId}`),
  
  // Cancel a delivery
  cancel: (externalDeliveryId) => api.post(`/delivery/cancel/${externalDeliveryId}`),
  
  // Get delivery info for an order
  getOrderDelivery: (orderId) => api.get(`/delivery/order/${orderId}`),
};
